/**
 * Affiliate Links Table
 * 
 * Displays all campaign links with statistics, copy functionality, and actions.
 */

import { useState } from "react";
import { cn, formatCurrency } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  SearchIcon,
  ArrowUpDownIcon,
  CopyIcon,
  CheckIcon,
  PlusIcon,
  SettingsIcon,
  QrCodeIcon,
} from "lucide-react";
import { toast } from "sonner";
import type { AffiliateLink } from "../types";
import { useAffiliateLinks, useUpdateLink } from "../hooks/use-affiliate-data";

import { API_BASE_URL } from "@/backend/config";

// Use the redirect service URL (via Doppler in production)
// This goes through the API to track clicks, then redirects to /invite/:slug
const REDIRECT_BASE_URL = import.meta.env.VITE_REDIRECT_BASE_URL || API_BASE_URL;

type AffiliateLinksTableProps = {
  onCreateLink: () => void;
  onEditLink: (link: AffiliateLink) => void;
  onShowQr: (link: AffiliateLink) => void;
};

type SortKey = "name" | "clickCount" | "referralCount" | "totalEarnings" | "createdAt";
type SortDirection = "asc" | "desc";

export const AffiliateLinksTable = ({
  onCreateLink,
  onEditLink,
  onShowQr,
}: AffiliateLinksTableProps) => {
  const { data: links = [], isLoading } = useAffiliateLinks();
  const updateLink = useUpdateLink();
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleToggleActive = async (link: AffiliateLink) => {
    try {
      await updateLink.mutateAsync({
        linkId: link.id,
        is_active: !link.isActive,
      });
      toast.success(link.isActive ? "Link paused" : "Link activated");
    } catch {
      toast.error("Failed to update link status");
    }
  };

  const filteredLinks = links.filter((link) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      link.name.toLowerCase().includes(searchLower) ||
      link.slug.toLowerCase().includes(searchLower)
    );
  });

  const sortedLinks = [...filteredLinks].sort((a, b) => {
    let aVal: number | string = 0;
    let bVal: number | string = 0;

    switch (sortKey) {
      case "name":
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
        break;
      case "clickCount":
        aVal = a.clickCount;
        bVal = b.clickCount;
        break;
      case "referralCount":
        aVal = a.referralCount;
        bVal = b.referralCount;
        break;
      case "totalEarnings":
        aVal = a.totalEarnings;
        bVal = b.totalEarnings;
        break;
      case "createdAt":
        aVal = new Date(a.createdAt).getTime();
        bVal = new Date(b.createdAt).getTime();
        break;
    }

    if (sortDirection === "asc") {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("desc");
    }
  };

  const handleCopy = async (link: AffiliateLink) => {
    const url = `${REDIRECT_BASE_URL}/r/${link.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(link.id);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error("Failed to copy link");
    }
  };

  const SortableHeader = ({
    label,
    sortKeyName,
    className,
  }: {
    label: string;
    sortKeyName: SortKey;
    className?: string;
  }) => (
    <button
      onClick={() => handleSort(sortKeyName)}
      className={cn(
        "flex items-center gap-1 text-xs font-semibold uppercase tracking-wider transition-colors hover:text-foreground",
        sortKey === sortKeyName ? "text-foreground" : "text-muted-foreground",
        className
      )}
    >
      {label}
      <ArrowUpDownIcon className="size-3" />
    </button>
  );

  if (isLoading) {
    return (
      <div className="rounded-xl bg-card p-4 md:p-6 border border-border/50 shadow-sm dark:shadow-none">
        <div className="animate-pulse">
          <div className="h-5 w-40 bg-muted rounded mb-4" />
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-14 bg-muted/50 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-card border border-border/50 shadow-sm dark:shadow-none overflow-hidden">
      {/* Header */}
      <div className="p-4 md:p-6 border-b border-border/50">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold">Campaign Links</h3>
            <p className="text-xs text-muted-foreground">
              {links.length} {links.length === 1 ? "link" : "links"} • Track performance per campaign
            </p>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-initial">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                placeholder="Search links..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 w-full sm:w-56"
              />
            </div>
            <Button onClick={onCreateLink} size="sm" className="shrink-0 gap-1.5">
              <PlusIcon className="size-4" />
              <span className="hidden sm:inline">New Link</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30">
            <tr>
              <th className="px-4 md:px-6 py-3 text-left">
                <SortableHeader label="Campaign" sortKeyName="name" />
              </th>
              <th className="px-4 md:px-6 py-3 hidden lg:table-cell">
                <div className="flex justify-center">
                  <SortableHeader label="Clicks" sortKeyName="clickCount" />
                </div>
              </th>
              <th className="px-4 md:px-6 py-3 hidden md:table-cell">
                <div className="flex justify-center">
                  <SortableHeader label="Referrals" sortKeyName="referralCount" />
                </div>
              </th>
              <th className="px-4 md:px-6 py-3 hidden lg:table-cell">
                <div className="flex justify-center">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Conv. Rate
                  </span>
                </div>
              </th>
              <th className="px-4 md:px-6 py-3">
                <div className="flex justify-end">
                  <SortableHeader label="Earnings" sortKeyName="totalEarnings" />
                </div>
              </th>
              <th className="px-4 md:px-6 py-3 hidden sm:table-cell">
                <div className="flex justify-center">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Status
                  </span>
                </div>
              </th>
              <th className="px-4 md:px-6 py-3 text-right">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Actions
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedLinks.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 md:px-6 py-8 text-center text-muted-foreground">
                  {search ? "No links match your search" : "No campaign links yet. Create your first one!"}
                </td>
              </tr>
            ) : (
              sortedLinks.map((link) => (
                <tr
                  key={link.id}
                  className={cn(
                    "border-t border-border/30 hover:bg-muted/20 transition-colors",
                    !link.isActive && "opacity-60"
                  )}
                >
                  {/* Campaign Name + Link */}
                  <td className="px-4 md:px-6 py-3">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium shrink-0",
                        link.isDefault 
                          ? "bg-primary/10 text-primary" 
                          : "bg-muted/50 text-muted-foreground"
                      )}>
                        {link.isDefault ? "★" : link.name[0].toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {link.name}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          /r/{link.slug}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* Clicks */}
                  <td className="px-4 md:px-6 py-3 text-center hidden lg:table-cell">
                    <div className="flex items-center justify-center">
                      <span className="text-sm font-medium">{link.clickCount.toLocaleString()}</span>
                    </div>
                  </td>

                  {/* Referrals */}
                  <td className="px-4 md:px-6 py-3 text-center hidden md:table-cell">
                    <div className="flex items-center justify-center">
                      <span className="text-sm font-medium">{link.referralCount.toLocaleString()}</span>
                    </div>
                  </td>

                  {/* Conversion Rate */}
                  <td className="px-4 md:px-6 py-3 text-center hidden lg:table-cell">
                    <div className="flex items-center justify-center">
                      <span className={cn(
                        "text-sm font-medium",
                        link.conversionRate > 5 ? "text-emerald-500" : ""
                      )}>
                        {link.conversionRate.toFixed(1)}%
                      </span>
                    </div>
                  </td>

                  {/* Earnings */}
                  <td className="px-4 md:px-6 py-3 text-right">
                    <span className="text-sm font-medium text-emerald-500">
                      +${formatCurrency(link.totalEarnings, 2)}
                    </span>
                    {link.performanceBonusEarnings > 0 && (
                      <div className="text-[10px] text-muted-foreground">
                        ${formatCurrency(link.performanceBonusEarnings, 2)} bonus
                      </div>
                    )}
                  </td>

                  {/* Status Toggle */}
                  <td className="px-4 md:px-6 py-3 hidden sm:table-cell">
                    <div className="flex items-center justify-center">
                      <Switch
                        checked={link.isActive}
                        onCheckedChange={() => handleToggleActive(link)}
                        disabled={link.isDefault || updateLink.isPending}
                        className="data-[state=checked]:bg-emerald-500"
                      />
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="px-4 md:px-6 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopy(link)}
                        className="h-8 w-8 p-0"
                        title="Copy link"
                      >
                        {copiedId === link.id ? (
                          <CheckIcon className="size-4 text-emerald-500" />
                        ) : (
                          <CopyIcon className="size-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onShowQr(link)}
                        className="h-8 w-8 p-0 hidden sm:flex"
                        title="Show QR code"
                      >
                        <QrCodeIcon className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEditLink(link)}
                        className="h-8 w-8 p-0"
                        title="Edit link"
                      >
                        <SettingsIcon className="size-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {/* Summary Footer - Inside same table for proper column alignment */}
          {sortedLinks.length > 0 && (
            <tfoot className="bg-muted/20 border-t border-border/30">
              <tr>
                <td className="px-4 md:px-6 py-4">
                  <span className="text-xs text-muted-foreground font-medium">Totals</span>
                </td>
                <td className="px-4 md:px-6 py-4 text-center hidden lg:table-cell">
                  <p className="text-xs text-muted-foreground">Total Clicks</p>
                  <p className="text-lg font-bold">
                    {sortedLinks.reduce((sum, l) => sum + l.clickCount, 0).toLocaleString()}
                  </p>
                </td>
                <td className="px-4 md:px-6 py-4 text-center hidden md:table-cell">
                  <p className="text-xs text-muted-foreground">Total Referrals</p>
                  <p className="text-lg font-bold">
                    {sortedLinks.reduce((sum, l) => sum + l.referralCount, 0).toLocaleString()}
                  </p>
                </td>
                <td className="px-4 md:px-6 py-4 text-center hidden lg:table-cell">
                  <p className="text-xs text-muted-foreground">Avg. Conversion</p>
                  <p className="text-lg font-bold">
                    {(sortedLinks.reduce((sum, l) => sum + l.conversionRate, 0) / sortedLinks.length || 0).toFixed(1)}%
                  </p>
                </td>
                <td className="px-4 md:px-6 py-4 text-right">
                  <p className="text-xs text-muted-foreground">Total Earnings</p>
                  <p className="text-lg font-bold text-emerald-500">
                    ${formatCurrency(sortedLinks.reduce((sum, l) => sum + l.totalEarnings, 0), 2)}
                  </p>
                </td>
                <td className="px-4 md:px-6 py-4 hidden sm:table-cell"></td>
                <td className="px-4 md:px-6 py-4"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

